import type { LogsInitConfiguration } from '@datadog/browser-logs';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import type { Attributes, Span } from '@opentelemetry/api';
/* eslint-disable-next-line @typescript-eslint/consistent-type-imports */
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { type FetchInstrumentationConfig } from '@opentelemetry/instrumentation-fetch/build/src/fetch';
import type { XMLHttpRequestInstrumentationConfig } from '@opentelemetry/instrumentation-xml-http-request';

type OTLPTraceExporterConfig = ConstructorParameters<typeof OTLPTraceExporter>[0];

export type DatadogConfig = {
    appId: string;
    clientToken: string;
    service?: string;
    enableSessionReplays?: boolean;
    forwardErrorsToLogs?: boolean;
    forwardConsoleLogs?: LogsInitConfiguration['forwardConsoleLogs'];
    beforeSend?: RumInitConfiguration['beforeSend'];
    version?: string;
    /** A flag to indicate whether feature flags should be sent to datadog */
    sendFeatureFlags?: boolean;
};

export type MetricsConfiguration = {
    segmentWriteKey?: string;
    datadogConfig?: DatadogConfig;
    env?: string;
};

/**
 * Type for initializing a OpenTelemetry WebTracerProvider and its parts.
 * @param name sets the `service.name` value for the Tracer.
 * @param version sets the `service.version` value for the Tracer.
 * @param auth sets the Tracer `header.authorization` value as `Basic @auth` and expects an encoded credential e.g. `btoa('user:pass')` .
 * @param withConsoleExporter adds developer mode console logger to Tracer for non-production environments.
 * @param exporterConfig extension to be merged with `OTLPTraceExporter.constructor`. *  Resource constructor.
 * @param fetchConfig extension to be merged with `OTLPExporterConfigBase`.
 * @param resourceAttributes extension to be merged with `Resource.constructor`.
 * @param xhrConfig extension to be merged with `XMLHttpRequestInstrumentationConfig`.
 */
export type OpenTelemetryConfig = {
    name: string;
    version: string;
    auth?: string; // May become required if fine-grained authorization is mandated
    withConsoleExporter?: boolean;
    // Pass-through custom configurations
    exporterConfig?: OTLPTraceExporterConfig;
    fetchConfig?: FetchInstrumentationConfig;
    resourceAttributes?: Attributes; // See SemanticResourceAttributes.d.ts
    xhrConfig?: XMLHttpRequestInstrumentationConfig;
};

export type SpanOrNull = Span | null;
export type SpanSetter = (_: SpanOrNull) => void;
export type SpanContextWrapper = [SpanOrNull, SpanSetter];

export type UserContext = {
    userId?: string;
    orgId?: string;
    details?: any;
};

export type ExtendedProperties = Record<string, any>;

export enum TelemetryType {
    PageviewData = 'PageviewData',
}

export enum UserAction {
    ButtonClicked = 'Button Clicked',
    Login = 'Login',
    Logout = 'Logout',
    ChangeTheme = 'Change Theme',
}

export enum ActivityType {
    Authenticate = 'Authenticate',
    Authorise = 'Authorise',
    Copy = 'Copy',
    Create = 'Create',
    Delete = 'Delete',
    Export = 'Export',
    Import = 'Import',
    Move = 'Move',
    Process = 'Process',
    Print = 'Print',
    Restore = 'Restore',
    Search = 'Search',
    SoftDelete = 'SoftDelete',
    Unknown = 'Unknown',
    Update = 'Update',
    View = 'View',
}

export enum ProductName {
    BLOCKSYNC = 'BlockSync',
    CENTRAL = 'Central',
    EVO = 'Evo',
    UNKNOWN = 'Unknown',
}
